/**
 * gpsTracker — Capacitor/web GPS tracking primitives.
 *
 * Wraps @capacitor/geolocation (native) and navigator.geolocation (web)
 * behind a uniform interface, plus the math helpers needed to turn a raw
 * GPS sample stream into the activity record shape we store on history.
 *
 * Pass 1 is foreground-only: the watch stops if the screen locks. Background
 * tracking ships in Pass 2 (requires iOS UIBackgroundModes + Android
 * foreground-service permission).
 *
 * Shape contract: see meta.gps in completed activity record.
 */
import { Capacitor } from '@capacitor/core';

const isNative = () => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
};

// Lazy-load the native plugin so the web bundle never tries to resolve it.
let _Geo;
const getGeo = async () => {
  if (_Geo) return _Geo;
  if (isNative()) {
    const mod = await import('@capacitor/geolocation');
    _Geo = mod.Geolocation;
  }
  return _Geo;
};

// ---------- Permissions ----------

export const checkGpsPermission = async () => {
  if (isNative()) {
    try {
      const Geo = await getGeo();
      const res = await Geo.checkPermissions();
      return res.location || res.coarseLocation || 'prompt';
    } catch { return 'prompt'; }
  }
  // Web: Permissions API where available
  try {
    if (navigator?.permissions?.query) {
      const r = await navigator.permissions.query({ name: 'geolocation' });
      return r.state; // 'granted' | 'denied' | 'prompt'
    }
  } catch { /* fallthrough */ }
  return 'prompt';
};

export const requestGpsPermission = async () => {
  if (isNative()) {
    try {
      const Geo = await getGeo();
      const res = await Geo.requestPermissions({ permissions: ['location'] });
      return res.location || 'denied';
    } catch { return 'denied'; }
  }
  // Web: there's no explicit request — calling getCurrentPosition triggers the prompt.
  return new Promise((resolve) => {
    if (!navigator?.geolocation) return resolve('denied');
    navigator.geolocation.getCurrentPosition(
      () => resolve('granted'),
      (err) => resolve(err.code === 1 ? 'denied' : 'prompt'),
      { enableHighAccuracy: false, timeout: 10000 },
    );
  });
};

// ---------- Watch ----------

/**
 * Start a position watch. onSample fires with a compact sample on each fix.
 * Returns an opaque handle to pass to stopTracking().
 */
export const startTracking = async ({ onSample, onError }) => {
  const opts = { enableHighAccuracy: true, maximumAge: 0, timeout: 30000 };
  if (isNative()) {
    const Geo = await getGeo();
    const id = await Geo.watchPosition(opts, (pos, err) => {
      if (err) { onError?.(err); return; }
      if (pos?.coords) onSample(compactSample(pos));
    });
    return { kind: 'native', id };
  }
  // Web
  if (!navigator?.geolocation) {
    onError?.(new Error('Geolocation not supported'));
    return { kind: 'web', id: null };
  }
  const id = navigator.geolocation.watchPosition(
    (pos) => { if (pos?.coords) onSample(compactSample(pos)); },
    (err) => onError?.(err),
    opts,
  );
  return { kind: 'web', id };
};

export const stopTracking = async (handle) => {
  if (!handle || handle.id == null) return;
  if (handle.kind === 'native') {
    try {
      const Geo = await getGeo();
      await Geo.clearWatch({ id: handle.id });
    } catch { /* ignore */ }
  } else if (handle.kind === 'web') {
    try { navigator.geolocation.clearWatch(handle.id); } catch { /* ignore */ }
  }
};

// ---------- Sample shape ----------

// Compact field names keep raw-point storage close to 80 bytes JSON per sample.
const compactSample = (pos) => ({
  t: pos.timestamp || Date.now(),
  la: pos.coords.latitude,
  lo: pos.coords.longitude,
  al: pos.coords.altitude ?? null,
  ac: pos.coords.accuracy ?? null,
  sp: pos.coords.speed ?? null,
  hd: pos.coords.heading ?? null,
});

// ---------- Math: haversine, splits, simplifier ----------

const R_EARTH_M = 6371000;
const toRad = (deg) => (deg * Math.PI) / 180;

export const haversineMeters = (lat1, lon1, lat2, lon2) => {
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R_EARTH_M * Math.asin(Math.min(1, Math.sqrt(a)));
};

// Douglas-Peucker polyline simplifier. Returns [[lat,lng], …].
const perpDistance = (p, a, b) => {
  const [ax, ay] = a, [bx, by] = b, [px, py] = p;
  const dx = bx - ax, dy = by - ay;
  if (dx === 0 && dy === 0) return Math.hypot(px - ax, py - ay);
  const t = ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy);
  const cx = ax + t * dx, cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy);
};

const dpReduce = (points, tolerance) => {
  if (points.length < 3) return points.slice();
  let maxDist = 0, idx = 0;
  for (let i = 1; i < points.length - 1; i++) {
    const d = perpDistance(points[i], points[0], points[points.length - 1]);
    if (d > maxDist) { maxDist = d; idx = i; }
  }
  if (maxDist > tolerance) {
    const left = dpReduce(points.slice(0, idx + 1), tolerance);
    const right = dpReduce(points.slice(idx), tolerance);
    return left.slice(0, -1).concat(right);
  }
  return [points[0], points[points.length - 1]];
};

/**
 * Reduce a raw sample stream to ~targetPoints anchor points for route preview.
 * Tolerance is in degrees; we auto-tune by iteratively halving until we hit
 * the target count (cheap, runs once per session at stop time).
 */
export const simplifyPolyline = (samples, targetPoints = 50) => {
  const points = samples.map((s) => [s.la, s.lo]);
  if (points.length <= targetPoints) return points;
  let tolerance = 0.00005;
  let out = dpReduce(points, tolerance);
  let iters = 0;
  while (out.length > targetPoints && iters < 20) {
    tolerance *= 1.7;
    out = dpReduce(points, tolerance);
    iters += 1;
  }
  return out;
};

/**
 * Compute mi or km splits from the sample stream, honoring pauseSegments.
 * Returns: [{ n, paceSecPerUnit, durationS, gainM, distanceM }, …]
 */
export const computeSplits = (samples, units, pauseSegments = []) => {
  const unitMeters = units === 'metric' ? 1000 : 1609.344;
  const splits = [];
  if (samples.length < 2) return splits;

  const isInPause = (t) =>
    pauseSegments.some(([s, e]) => s != null && e != null && t >= s && t <= e);

  let acc = 0;          // distance into current split
  let splitStartT = samples[0].t;
  let splitStartElev = samples[0].al;
  let gainInSplit = 0;
  let prevElev = samples[0].al;
  let splitNum = 1;

  for (let i = 1; i < samples.length; i++) {
    const prev = samples[i - 1];
    const cur = samples[i];
    if (isInPause(cur.t) || isInPause(prev.t)) { prevElev = cur.al; continue; }
    const seg = haversineMeters(prev.la, prev.lo, cur.la, cur.lo);

    if (prev.al != null && cur.al != null) {
      const dEl = cur.al - prev.al;
      if (dEl > 0) gainInSplit += dEl;
    }

    if (acc + seg >= unitMeters) {
      // close split at proportional point
      const remaining = unitMeters - acc;
      const frac = remaining / seg;
      const splitEndT = prev.t + (cur.t - prev.t) * frac;
      const durS = Math.max(1, (splitEndT - splitStartT) / 1000);
      splits.push({
        n: splitNum,
        unit: units === 'metric' ? 'km' : 'mi',
        paceSecPerUnit: Math.round(durS),
        durationS: Math.round(durS),
        gainM: Math.round(gainInSplit),
        distanceM: Math.round(unitMeters),
      });
      splitNum += 1;
      splitStartT = splitEndT;
      acc = seg - remaining;
      gainInSplit = 0;
      splitStartElev = cur.al;
    } else {
      acc += seg;
    }
    prevElev = cur.al;
  }
  return splits;
};

/**
 * Roll up the full sample stream into a finalized session record.
 *
 * Returns { record, rawPoints } where:
 *  - record: the shape for history entry's meta.gps + top-level distance / duration
 *  - rawPoints: the full samples array (stored separately in IndexedDB)
 */
export const finalizeSession = ({ samples, pauseSegments, startedAt, endedAt, units }) => {
  const isInPause = (t) =>
    pauseSegments.some(([s, e]) => s != null && e != null && t >= s && t <= e);

  let distanceM = 0;
  let movingMs = 0;
  let elevGain = 0;
  let elevLoss = 0;
  let maxSpeed = 0;

  for (let i = 1; i < samples.length; i++) {
    const prev = samples[i - 1];
    const cur = samples[i];
    if (isInPause(cur.t) || isInPause(prev.t)) continue;
    const seg = haversineMeters(prev.la, prev.lo, cur.la, cur.lo);
    distanceM += seg;
    movingMs += cur.t - prev.t;
    if (prev.al != null && cur.al != null) {
      const dEl = cur.al - prev.al;
      if (dEl > 0) elevGain += dEl; else elevLoss += -dEl;
    }
    const spd = cur.sp ?? (seg / Math.max(0.001, (cur.t - prev.t) / 1000));
    if (spd > maxSpeed) maxSpeed = spd;
  }

  const durationS = Math.max(1, Math.round((endedAt - startedAt) / 1000));
  const movingS = Math.max(1, Math.round(movingMs / 1000));
  const avgSpeedMs = distanceM / movingS;
  const unitMeters = units === 'metric' ? 1000 : 1609.344;
  const distanceUnits = distanceM / unitMeters;
  const avgPaceSecPerUnit = distanceUnits > 0 ? movingS / distanceUnits : 0;

  return {
    record: {
      version: 1,
      hasRawPoints: samples.length > 0,
      polyline: simplifyPolyline(samples, 50),
      stats: {
        distanceM: Math.round(distanceM),
        durationS,
        movingTimeS: movingS,
        avgPaceSecPerUnit: Math.round(avgPaceSecPerUnit),
        avgSpeedKmh: Math.round(avgSpeedMs * 3.6 * 10) / 10,
        maxSpeedKmh: Math.round(maxSpeed * 3.6 * 10) / 10,
        elevationGainM: Math.round(elevGain),
        elevationLossM: Math.round(elevLoss),
        splits: computeSplits(samples, units, pauseSegments),
      },
      startedAt,
      endedAt,
      pauseSegments,
      sampleCount: samples.length,
      units,
    },
    rawPoints: samples,
  };
};

// ---------- Live readouts (used by the tracker UI) ----------

/**
 * Cumulative distance up through `samples[upTo-1]`, honoring pauseSegments.
 * Cheap O(n) — fine to call per-tick on a stream of a few thousand points.
 */
export const liveDistanceMeters = (samples, pauseSegments) => {
  if (samples.length < 2) return 0;
  const isInPause = (t) =>
    pauseSegments.some(([s, e]) => s != null && e != null && t >= s && t <= e);
  let d = 0;
  for (let i = 1; i < samples.length; i++) {
    const prev = samples[i - 1];
    const cur = samples[i];
    if (isInPause(cur.t) || isInPause(prev.t)) continue;
    d += haversineMeters(prev.la, prev.lo, cur.la, cur.lo);
  }
  return d;
};

/**
 * Recent-window pace: average pace over the last `windowMs` of unpaused samples.
 * Returns seconds per unit. 0 if not enough data.
 */
export const recentPaceSecPerUnit = (samples, pauseSegments, units, windowMs = 30000) => {
  if (samples.length < 2) return 0;
  const isInPause = (t) =>
    pauseSegments.some(([s, e]) => s != null && e != null && t >= s && t <= e);
  const cutoff = samples[samples.length - 1].t - windowMs;
  let d = 0, ms = 0;
  for (let i = samples.length - 1; i > 0; i--) {
    const prev = samples[i - 1];
    const cur = samples[i];
    if (prev.t < cutoff) break;
    if (isInPause(cur.t) || isInPause(prev.t)) continue;
    d += haversineMeters(prev.la, prev.lo, cur.la, cur.lo);
    ms += cur.t - prev.t;
  }
  const unitMeters = units === 'metric' ? 1000 : 1609.344;
  const units_ = d / unitMeters;
  if (units_ <= 0 || ms <= 0) return 0;
  return (ms / 1000) / units_;
};

// ---------- Formatting helpers ----------

export const fmtPace = (secPerUnit) => {
  if (!secPerUnit || !isFinite(secPerUnit)) return '—';
  const m = Math.floor(secPerUnit / 60);
  const s = Math.round(secPerUnit % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export const fmtDistance = (meters, units) => {
  const unitMeters = units === 'metric' ? 1000 : 1609.344;
  return (meters / unitMeters).toFixed(2);
};

export const fmtElapsed = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n) => n.toString().padStart(2, '0');
  return hh > 0 ? `${hh}:${pad(mm)}:${pad(ss)}` : `${mm}:${pad(ss)}`;
};
